/**
 * Taking part of a leg off — ROADMAP 5.15.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A FRACTION, NEVER A QUANTITY. THIS IS THE WHOLE DESIGN.
 *
 * `exitPlan.ts` states the rule the exit path is built on: the caller names
 * WHICH position to close, never HOW MUCH. The drawer's numbers are from its
 * last read, and between that read and the click a resting stop can fire, a
 * guardian can exit, or the same account can be traded from another tab. A
 * browser that said "sell 2 lots" against a position that is now 1 lot does
 * not fail — it OPENS A NEW SHORT. The trader pressed a button labelled Exit
 * and acquired a position.
 *
 * A PERCENTAGE survives that completely. "Half" is still half whatever the
 * leg turns out to hold, so the browser can offer 25/50/75/100 without ever
 * asserting a size, and the server resolves it against a read taken at the
 * moment of acting. The invariant is untouched and the trader gets the
 * control they asked for.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ROUNDING: NEAREST, AT LEAST ONE LOT, NEVER MORE THAN IS HELD
 *
 * Lots are indivisible, so 50% of 7 lots is 3.5 and something has to give.
 *
 *   NEAREST, not floor. Floor is the reflex — "never sell what is not held" —
 *   but that danger is handled by the cap below, and flooring means 50% of 7
 *   closes 3 while the trader watches 4 lots stay on. Rounding up on the half
 *   closes slightly more, and closing slightly more of a position you decided
 *   to halve is the error that costs nothing.
 *
 *   AT LEAST ONE LOT, because 25% of 1 lot is 0.25 and a button that sends
 *   nothing is a button that looks broken. One lot IS the whole leg there,
 *   which is the only honest answer a quarter can have.
 *
 *   NEVER MORE THAN IS HELD. The cap is what makes the two rules above safe:
 *   whatever the arithmetic produces, it cannot exceed the open lots, so no
 *   rounding can turn an exit into an opening order.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/** The fractions a trader can pick. Quarters, which is how a desk talks. */
export const EXIT_FRACTIONS = [0.25, 0.5, 0.75, 1];
export const isExitFraction = (v) => typeof v === 'number' && EXIT_FRACTIONS.includes(v);
/**
 * How many LOTS a fraction of a leg comes to.
 *
 * `openLots` is what the leg holds right now, from whoever is asking — the
 * server from its own read, the dialog from the book it is showing. The two
 * can disagree for a moment and that is fine: the dialog's figure is a
 * preview, and only the server's is sent.
 *
 * Zero in, zero out. A leg holding nothing has no fraction to take.
 */
export function exitLots(openLots, fraction) {
    if (!Number.isFinite(openLots) || openLots <= 0)
        return 0;
    const whole = Math.floor(openLots);
    if (whole <= 0)
        return 0;
    if (fraction >= 1)
        return whole;
    return Math.min(whole, Math.max(1, Math.round(whole * fraction)));
}
/** "50%" — the label on the toggle, and in the order's own description. */
export const exitFractionLabel = (fraction) => `${Math.round(fraction * 100)}%`;
export function scaleInLots(leg, fraction) {
    const lotSize = Math.max(1, leg.lotSize);
    const held = Math.floor(Math.abs(leg.netQty) / lotSize);
    if (held <= 0 || !(fraction > 0))
        return 0;
    if (fraction >= 1)
        return held;
    return Math.max(1, Math.round(held * fraction));
}
/** The units that many lots is, for the line under the buttons. */
export const scaleInQty = (leg, fraction) => scaleInLots(leg, fraction) * Math.max(1, leg.lotSize);
//# sourceMappingURL=partialExit.js.map